import { useCallback, useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { Layers, Sparkles, Save, FileJson, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, fmtDate, usePolling } from "@/lib/api";
import { PageHeader, Panel, EmptyState, Spinner, Alert, WorkingTerminal } from "@/components/common";
import PlanView from "@/components/plan/PlanView";

const GeneratorForm = ({ status, onCreated, disabled, profileBudget }) => {
  const [budget, setBudget] = useState("");
  const [goals, setGoals] = useState("");
  const canUseAds = Boolean(status?.connected && status?.selected_account);
  const [useAds, setUseAds] = useState(true);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/plans", { monthly_budget: budget ? Number(budget) : null, goals: goals || null, use_ads_data: canUseAds && useAds });
      onCreated(data);
      toast.message("Claude Sonnet 5.5 is drafting your campaign plan…");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel title="Plan inputs" icon={Sparkles} testid="plan-generator-panel" className="mb-6">
      <form onSubmit={submit} className="grid gap-5 lg:grid-cols-6">
        <label className="lg:col-span-1"><span className="cc-eyebrow">Monthly budget (SGD)</span>
          <input data-testid="plan-budget-input" type="number" min="0" step="50" className="cc-input mt-2 font-mono" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder={profileBudget ? `Profile: ${profileBudget}` : "AI suggests"} /></label>
        <label className="lg:col-span-3"><span className="cc-eyebrow">Goals / notes (optional)</span>
          <input data-testid="plan-goals-input" className="cc-input mt-2" value={goals} onChange={(e) => setGoals(e.target.value)} placeholder="e.g. More back-pain bookings near Tanjong Pagar" /></label>
        <div className="lg:col-span-1"><span className="cc-eyebrow">Use Ads data</span>
          <div className="mt-3 flex items-center gap-2">
            <Switch data-testid="plan-use-ads-switch" checked={canUseAds && useAds} disabled={!canUseAds} onCheckedChange={setUseAds} />
            <span className="text-xs text-slate-400">{canUseAds ? "Learn from account" : "Not connected"}</span>
          </div></div>
        <div className="flex items-end lg:col-span-1">
          <button data-testid="plan-generate-button" type="submit" disabled={busy || disabled} className="cc-btn cc-btn-primary w-full">
            {busy ? <Spinner className="h-4 w-4 text-[#03130c]" /> : <Sparkles className="h-4 w-4" />} Generate
          </button>
        </div>
      </form>
    </Panel>
  );
};

export default function CampaignPlan() {
  const { status } = useOutletContext();
  const [hasProfile, setHasProfile] = useState(null);
  const [profileBudget, setProfileBudget] = useState(null);
  const [domain, setDomain] = useState("");
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async () => {
    const { data } = await api.get("/plans");
    setPlans(data.plans);
    return data.plans;
  }, []);
  const loadOne = useCallback(async (id) => {
    const { data } = await api.get(`/plans/${id}`);
    setCurrent(data);
    setDraft(data.plan ? structuredClone(data.plan) : null);
    setDirty(false);
  }, []);

  useEffect(() => {
    api.get("/profile").then(({ data }) => {
      setHasProfile(Boolean(data.profile_doc?.profile));
      setProfileBudget(data.profile_doc?.profile?.monthly_budget ?? null);
      setDomain((data.profile_doc?.website || data.default_website).replace(/^https?:\/\/(www\.)?/, "").replace(/\/$/, ""));
    });
    loadList().then((list) => list[0] && loadOne(list[0].id));
  }, [loadList, loadOne]);

  const generating = current?.status === "generating";
  const refresh = useCallback(() => current && loadOne(current.id).then(loadList), [current, loadOne, loadList]);
  usePolling(refresh, generating, 4000);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/plans/${current.id}`, { plan: draft });
      setCurrent(data);
      setDirty(false);
      toast.success(data.validation.all_valid ? "Plan saved · all ad copy within limits" : "Plan saved · some items still exceed limits");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(draft, null, 2)], { type: "application/json" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: `ads-monster-plan-${current.id}.json` });
    a.click();
  };

  if (hasProfile === null) return <Spinner />;

  return (
    <div data-testid="plan-page">
      <PageHeader eyebrow="AI strategist · send campaigns to Approvals via the Campaign Builder" title="Campaign Plan"
        description="A complete Search build — campaigns, ad groups, match-typed keywords, negatives, responsive search ads, budgets and bidding — drafted by Claude Sonnet 5.5 and validated against Google's character limits."
        actions={draft && (
          <>
            {plans.length > 1 && (
              <select data-testid="plan-history-select" className="cc-input w-auto font-mono text-xs" value={current?.id || ""} onChange={(e) => loadOne(e.target.value)}>
                {plans.map((p) => <option key={p.id} value={p.id}>{fmtDate(p.created_at)} · {p.status}</option>)}
              </select>
            )}
            <button data-testid="plan-export-button" onClick={exportJson} className="cc-btn cc-btn-ghost"><FileJson className="h-4 w-4" /> JSON</button>
            <button data-testid="plan-save-button" onClick={save} disabled={!dirty || saving} className="cc-btn cc-btn-primary">{saving ? <Spinner className="h-4 w-4 text-[#03130c]" /> : <Save className="h-4 w-4" />} Save edits</button>
          </>
        )} />
      {!hasProfile ? (
        <EmptyState testid="plan-profile-needed" icon={Building2} title="Build the business profile first" description="The plan is grounded in facts from therehablabsg.com. Crawl the site once, then come back.">
          <Link to="/profile" data-testid="plan-go-profile-button" className="cc-btn cc-btn-primary">Go to Business Profile</Link>
        </EmptyState>
      ) : (
        <>
          <GeneratorForm status={status} profileBudget={profileBudget} disabled={generating} onCreated={(p) => { setCurrent(p); setDraft(null); loadList(); }} />
          {generating && <WorkingTerminal testid="plan-progress" title="Claude Sonnet 5.5 is building the plan (1–3 min)" lines={["Mapping services to campaign structure", "Writing keywords with match types and negatives", "Writing RSAs and validating 30/90 character limits"]} />}
          {current?.status === "error" && <Alert testid="plan-failed" title="Plan generation failed">{current.error}</Alert>}
          {!current && <EmptyState testid="plan-empty-state" icon={Layers} title="No plan yet" description="Set an optional budget and goal, then generate. Works with or without Google Ads connected." />}
          {current?.status === "ready" && draft && (
            <>
              <div className="mb-4 font-mono text-xs text-slate-500" data-testid="plan-meta">{fmtDate(current.completed_at)} · {current.model} · {current.used_ads_data ? "learned from Google Ads data" : "business profile only"}{current.updated_at ? ` · edited ${fmtDate(current.updated_at)}` : ""}</div>
              <PlanView plan={draft} validation={current.validation} domain={domain} onChange={(p) => { setDraft(p); setDirty(true); }} />
            </>
          )}
        </>
      )}
    </div>
  );
}
